import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useCart } from '../hooks/useCart'
import { COMMON_ITEM_NAMES, CATEGORY_LABELS, CATEGORIES } from '../data/commonItems'
import type { Product } from '../types'
import { playScanSound } from '../services/sounds'
import { hapticLight } from '../utils/haptic'
import { Toast } from '../components/Toast'
import { QuantityPicker } from '../components/QuantityPicker'
import * as db from '../services/db'

const HINT_KEY = 'hasSeenHint'

export function HomeView() {
  const { products, itemCount, addByProductIdWithQuantity } = useCart()
  const [commonProducts, setCommonProducts] = useState<Product[]>([])
  const [quantityProduct, setQuantityProduct] = useState<Product | null>(null)
  const [toastVisible, setToastVisible] = useState(false)
  const [toastMessage, setToastMessage] = useState('Added! 🎉')
  const [showHint, setShowHint] = useState(false)

  useEffect(() => {
    const all = Array.from(products.values())
    const common = COMMON_ITEM_NAMES.map(
      (name) => all.find((p) => p.name === name)
    ).filter((p): p is Product => p != null)
    setCommonProducts(common)
  }, [products])

  useEffect(() => {
    db.getSetting(HINT_KEY).then((v) => setShowHint(v !== 'true'))
  }, [])

  const dismissHint = useCallback(() => {
    setShowHint(false)
    db.setSetting(HINT_KEY, 'true')
  }, [])

  const handleTapItem = (product: Product) => {
    setQuantityProduct(product)
  }

  const handleQuantityAdd = async (quantity: number) => {
    if (!quantityProduct) return
    const ok = await addByProductIdWithQuantity(quantityProduct.id, quantity)
    if (ok) {
      playScanSound()
      hapticLight()
      setToastMessage(quantity > 1 ? `${quantity} added! 🎉` : 'Added! 🎉')
      setToastVisible(true)
    }
    setQuantityProduct(null)
  }

  return (
    <div className="page page--home">
      <a href="#main-content" className="skip-link">Skip to scan</a>
      {showHint && (
        <div className="hint-overlay" role="dialog" aria-labelledby="hint-title" aria-modal="true">
          <div className="hint-overlay-content">
            <h2 id="hint-title" className="hint-overlay-title">👋 Welcome!</h2>
            <p className="hint-overlay-p">Tap items or scan QR codes to add groceries to your cart. When you’re done, tap the cart and pay.</p>
            <button type="button" className="btn-large btn-primary hint-overlay-btn" onClick={dismissHint}>
              Got it!
            </button>
          </div>
        </div>
      )}
      {quantityProduct && (
        <QuantityPicker
          product={quantityProduct}
          onAdd={handleQuantityAdd}
          onCancel={() => setQuantityProduct(null)}
        />
      )}
      <Toast message={toastMessage} visible={toastVisible} onDismiss={() => setToastVisible(false)} />
      <header className="home-header">
        <h1 className="home-title">🛒 Grocery Store</h1>
        <Link
          to="/cart"
          className="home-cart-link"
          aria-label={`Cart, ${itemCount} items`}
        >
          <span className="home-cart-icon" aria-hidden>🛒</span>
          {itemCount > 0 && (
            <span className="home-cart-badge">{itemCount}</span>
          )}
        </Link>
      </header>

      <section id="main-content" className="home-scan">
        <Link to="/scan" className="btn-scan" aria-label="Scan item with camera">
          <span className="btn-scan-icon" aria-hidden>📷</span>
          <span>Scan item</span>
        </Link>
      </section>

      <section className="home-section" aria-labelledby="categories-heading">
        <h2 id="categories-heading" className="home-section-title">Categories</h2>
        <div className="home-categories">
          {CATEGORIES.map((cat) => (
            <Link
              key={cat}
              to={`/category/${encodeURIComponent(cat)}`}
              className="home-category-card"
              aria-label={`Browse ${cat}`}
            >
              {CATEGORY_LABELS[cat] ?? cat}
            </Link>
          ))}
        </div>
      </section>

      <section className="home-section" aria-labelledby="quick-add-heading">
        <h2 id="quick-add-heading" className="home-section-title">Quick add</h2>
        <p className="home-section-sub">Tap to add to your cart</p>
        <div className="home-quick-grid">
          {commonProducts.map((product) => (
            <button
              key={product.id}
              type="button"
              className="home-quick-item"
              onClick={() => handleTapItem(product)}
              aria-label={`Add ${product.name} to cart, $${product.price.toFixed(2)}`}
            >
              <span className="home-quick-emoji" aria-hidden>{product.imagePath}</span>
              <span className="home-quick-name">{product.name}</span>
              <span className="home-quick-price">${product.price.toFixed(2)}</span>
            </button>
          ))}
        </div>
      </section>

      <footer className="home-footer">
        <Link to="/parent" className="home-parent-link">Parent</Link>
      </footer>
    </div>
  )
}
